import { useCallback, useEffect, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { AxiosError } from "axios";
import {
  adminApi,
  refreshAdminToken,
  setAdminToken,
  type ReferralAdminCodeDetail,
  type ReferralAdminCodeSaveParam,
  type ReferralAdminSettlementResult,
} from "api/adminApi";
import { type ReferralAmbassadorDashboardResult } from "api/ambassadorApi";

type AuthPhase = "checking" | "login" | "denied" | "ready";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const currentMonth = () => new Date().toISOString().slice(0, 7);

// 레퍼럴 어드민 — @orwellhealth.org 구글 계정 중 서버 허용 목록만 접근 가능.
// referral 전체 타입을 관리한다: affiliate(외부 명칭 "앰버서더", 정산 대상) + discount(단순할인).
// 코드 발급/수정(유형·이름·기간·이메일·단가), 코드별 성과, 월별 정산 표.
// 인증은 어드민 전용 인스턴스(adminApi)가 관리 — 기존 유저/프로모션 세션과 완전 분리.
export default function AdminPage() {
  const [phase, setPhase] = useState<AuthPhase>("checking");
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<ReferralAdminCodeDetail[]>([]);

  const enter = useCallback(async () => {
    try {
      const res = await adminApi.codeList();
      if (res.code === 0) {
        setCodes(res.result ?? []);
        setPhase("ready");
      } else {
        setError(res.message || FALLBACK_ERROR_MESSAGE);
        setPhase("login");
      }
    } catch (e) {
      const status = (e as AxiosError).response?.status;
      setPhase(status === 403 ? "denied" : "login");
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = await refreshAdminToken();
      if (!token) {
        setPhase("login");
        return;
      }
      await enter();
    })();
  }, [enter]);

  const reloadCodes = async () => {
    const res = await adminApi.codeList();
    if (res.code === 0) setCodes(res.result ?? []);
  };

  const handleGoogleLogin = async (credential?: string) => {
    if (!credential) return;
    setError(null);
    try {
      const res = await adminApi.login(credential);
      if (res.code === 0 && res.result?.accessToken) {
        setAdminToken(res.result.accessToken);
        await enter();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-[960px] px-5 py-10">
        <h1 className="text-xl font-bold text-gray-900">디스턴싱 어드민</h1>

        {phase === "checking" && (
          <p className="mt-10 text-sm text-gray-500">확인 중...</p>
        )}

        {phase === "login" && (
          <div className="mt-10 flex flex-col items-start gap-4">
            <p className="text-sm text-gray-500">
              @orwellhealth.org 구글 계정으로 로그인해 주세요.
            </p>
            <GoogleLogin
              onSuccess={(response) => handleGoogleLogin(response.credential)}
              onError={() => setError(FALLBACK_ERROR_MESSAGE)}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        )}

        {phase === "denied" && (
          <p className="mt-10 text-sm text-red-500">
            권한이 없는 계정이에요. 관리자에게 어드민 등록을 요청해 주세요.
          </p>
        )}

        {phase === "ready" && (
          <div className="mt-8 flex flex-col gap-10">
            <CodeSection codes={codes} onSaved={reloadCodes} />
            <PerformanceSection codes={codes} />
            <SettlementSection />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- 코드 관리 ----------

type CodeFormState = {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  ambassadorEmail: string;
  perPayerAmount: string;
};

const emptyForm: CodeFormState = {
  code: "",
  name: "",
  type: "affiliate",
  isActive: true,
  startDate: "",
  endDate: "",
  ambassadorEmail: "",
  perPayerAmount: "",
};

// 앰버서더는 affiliate 의 외부 명칭 — 화면 표기는 앰버서더/할인으로 통일
const typeLabel = (type: string) => (type === "discount" ? "할인" : "앰버서더");

function CodeSection({
  codes,
  onSaved,
}: {
  codes: ReferralAdminCodeDetail[];
  onSaved: () => Promise<void>;
}) {
  // editingCode: null=폼 닫힘, ""=신규, 그 외=해당 코드 수정
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<CodeFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const isNew = editingCode === "";

  const openNew = () => {
    setEditingCode("");
    setForm(emptyForm);
    setMessage(null);
  };

  const openEdit = (detail: ReferralAdminCodeDetail) => {
    setEditingCode(detail.code);
    setForm({
      code: detail.code,
      name: detail.name ?? "",
      type: detail.type,
      isActive: detail.isActive,
      startDate: detail.startDate ?? "",
      endDate: detail.endDate ?? "",
      ambassadorEmail: detail.ambassadorEmail ?? "",
      perPayerAmount:
        detail.perPayerAmount != null ? String(detail.perPayerAmount) : "",
    });
    setMessage(null);
  };

  const save = async () => {
    if (saving || form.code.trim().length === 0) return;
    setSaving(true);
    setMessage(null);
    const param: ReferralAdminCodeSaveParam = {
      code: form.code.trim(),
      name: form.name.trim() || null,
      type: form.type,
      isActive: form.isActive,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      ambassadorEmail: form.ambassadorEmail.trim() || null,
      perPayerAmount: form.perPayerAmount ? Number(form.perPayerAmount) : null,
    };
    try {
      const res = isNew
        ? await adminApi.codeCreate(param)
        : await adminApi.codeUpdate(param);
      if (res.code === 0) {
        setEditingCode(null);
        await onSaved();
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const inputClassName =
    "rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-900";

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">코드 관리</h2>
        <button
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white"
          onClick={openNew}
        >
          새 코드 발급
        </button>
      </div>

      {editingCode !== null && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-900">
            {isNew ? "새 코드 발급" : `코드 수정 — ${editingCode}`}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              코드 (/with/코드)
              <input
                className={inputClassName}
                value={form.code}
                disabled={!isNew}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              표시 이름
              <input
                className={inputClassName}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              유형
              <select
                className={inputClassName}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="affiliate">앰버서더 (정산 대상)</option>
                <option value="discount">단순할인 (정산 없음)</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              앰버서더 이메일 (대시보드 계정)
              <input
                className={inputClassName}
                type="email"
                value={form.ambassadorEmail}
                onChange={(e) =>
                  setForm({ ...form, ambassadorEmail: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              결제자 1인당 정산 단가 (원)
              <input
                className={inputClassName}
                type="number"
                min={0}
                value={form.perPayerAmount}
                onChange={(e) =>
                  setForm({ ...form, perPayerAmount: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              시작일
              <input
                className={inputClassName}
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm({ ...form, startDate: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              만료일 (비우면 무기한)
              <input
                className={inputClassName}
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
          </div>
          {!isNew && (
            <label className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              활성 (끄면 /with/{form.code} 게이트가 무효 처리돼요)
            </label>
          )}
          {message && <p className="mt-3 text-sm text-red-500">{message}</p>}
          <div className="mt-4 flex gap-2">
            <button
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              disabled={saving || form.code.trim().length === 0}
              onClick={save}
            >
              {saving ? "저장 중..." : "저장"}
            </button>
            <button
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600"
              onClick={() => setEditingCode(null)}
            >
              취소
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
              <th className="px-3 py-3 font-medium">코드</th>
              <th className="px-3 py-3 font-medium">유형</th>
              <th className="px-3 py-3 font-medium">이름</th>
              <th className="px-3 py-3 font-medium">이메일</th>
              <th className="px-3 py-3 text-right font-medium">단가</th>
              <th className="px-3 py-3 font-medium">기간</th>
              <th className="px-3 py-3 font-medium">상태</th>
              <th className="px-3 py-3 font-medium">계정</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-400" colSpan={9}>
                  등록된 코드가 없어요
                </td>
              </tr>
            )}
            {codes.map((detail) => (
              <tr
                key={detail.code}
                className="border-b border-gray-100 last:border-b-0"
              >
                <td className="px-3 py-3 font-medium text-gray-900">
                  {detail.code}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {typeLabel(detail.type)}
                </td>
                <td className="px-3 py-3 text-gray-900">
                  {detail.name ?? "-"}
                </td>
                <td className="px-3 py-3 text-gray-900">
                  {detail.ambassadorEmail ?? "-"}
                </td>
                <td className="px-3 py-3 text-right text-gray-900">
                  {detail.perPayerAmount != null
                    ? `${detail.perPayerAmount.toLocaleString()}원`
                    : "-"}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {detail.startDate ?? "…"} ~ {detail.endDate ?? "무기한"}
                </td>
                <td className="px-3 py-3">
                  <span
                    className={
                      detail.isActive ? "text-green-600" : "text-gray-400"
                    }
                  >
                    {detail.isActive ? "활성" : "비활성"}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {detail.accountStatus}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    className="text-gray-500 underline"
                    onClick={() => openEdit(detail)}
                  >
                    수정
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------- 코드별 성과 ----------

function PerformanceSection({ codes }: { codes: ReferralAdminCodeDetail[] }) {
  const [selectedCode, setSelectedCode] = useState("");
  const [tab, setTab] = useState<"daily" | "monthly">("monthly");
  const [result, setResult] =
    useState<ReferralAmbassadorDashboardResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (code: string) => {
    setSelectedCode(code);
    setResult(null);
    setMessage(null);
    if (!code) return;
    setLoading(true);
    try {
      const res = await adminApi.performance(code);
      if (res.code === 0 && res.result) setResult(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const buckets = (tab === "daily" ? result?.daily : result?.monthly) ?? [];

  return (
    <section>
      <h2 className="text-base font-bold text-gray-900">코드별 성과</h2>
      <div className="mt-3 flex items-center gap-2">
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none"
          value={selectedCode}
          onChange={(e) => load(e.target.value)}
        >
          <option value="">코드 선택</option>
          {codes.map((detail) => (
            <option key={detail.code} value={detail.code}>
              {detail.code}
              {detail.name ? ` (${detail.name})` : ""}
            </option>
          ))}
        </select>
        {result && (
          <div className="flex gap-1">
            {(["monthly", "daily"] as const).map((t) => (
              <button
                key={t}
                className={`rounded-full px-3 py-1 text-sm ${
                  tab === t
                    ? "bg-gray-900 text-white"
                    : "border border-gray-200 bg-white text-gray-500"
                }`}
                onClick={() => setTab(t)}
              >
                {t === "monthly" ? "월별" : "일별"}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <p className="mt-3 text-sm text-gray-500">불러오는 중...</p>}
      {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

      {result && (
        <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
                <th className="px-3 py-3 font-medium">
                  {tab === "daily" ? "날짜" : "월"}
                </th>
                <th className="px-3 py-3 text-right font-medium">가입자</th>
                <th className="px-3 py-3 text-right font-medium">결제자</th>
                <th className="px-3 py-3 text-right font-medium">환불자</th>
                {tab === "monthly" && (
                  <th className="px-3 py-3 text-right font-medium">정산액</th>
                )}
              </tr>
            </thead>
            <tbody>
              {buckets.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-gray-400"
                    colSpan={tab === "monthly" ? 5 : 4}
                  >
                    아직 집계된 데이터가 없어요
                  </td>
                </tr>
              )}
              {buckets.map((bucket) => (
                <tr
                  key={bucket.period}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td className="px-3 py-3 text-gray-900">{bucket.period}</td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.signupCount}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.payerCount}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-900">
                    {bucket.refundCount}
                  </td>
                  {tab === "monthly" && (
                    <td className="px-3 py-3 text-right text-gray-900">
                      {bucket.settlementAmount != null
                        ? `${bucket.settlementAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------- 월별 정산 ----------

function SettlementSection() {
  const [month, setMonth] = useState(currentMonth());
  const [result, setResult] = useState<ReferralAdminSettlementResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (targetMonth: string) => {
    setMonth(targetMonth);
    setResult(null);
    setMessage(null);
    if (!targetMonth) return;
    setLoading(true);
    try {
      const res = await adminApi.settlement(targetMonth);
      if (res.code === 0 && res.result) setResult(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(currentMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900">월별 정산</h2>
        <input
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none"
          type="month"
          value={month}
          onChange={(e) => load(e.target.value)}
        />
      </div>

      {loading && <p className="mt-3 text-sm text-gray-500">불러오는 중...</p>}
      {message && <p className="mt-3 text-sm text-red-500">{message}</p>}

      {result && (
        <>
          <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
                  <th className="px-3 py-3 font-medium">코드</th>
                  <th className="px-3 py-3 font-medium">이름</th>
                  <th className="px-3 py-3 font-medium">이메일</th>
                  <th className="px-3 py-3 text-right font-medium">결제자</th>
                  <th className="px-3 py-3 text-right font-medium">환불자</th>
                  <th className="px-3 py-3 text-right font-medium">단가</th>
                  <th className="px-3 py-3 text-right font-medium">정산액</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-gray-400"
                      colSpan={7}
                    >
                      앰버서더 연결된 코드가 없어요
                    </td>
                  </tr>
                )}
                {result.rows.map((row) => (
                  <tr
                    key={row.code}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-3 py-3 font-medium text-gray-900">
                      {row.code}
                    </td>
                    <td className="px-3 py-3 text-gray-900">
                      {row.name ?? "-"}
                    </td>
                    <td className="px-3 py-3 text-gray-900">
                      {row.ambassadorEmail}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.payerCount}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.refundCount}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900">
                      {row.perPayerAmount != null
                        ? `${row.perPayerAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900">
                      {row.settlementAmount != null
                        ? `${row.settlementAmount.toLocaleString()}원`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-right text-sm font-semibold text-gray-900">
            합계 {result.totalAmount.toLocaleString()}원
          </p>
          <p className="mt-1 text-right text-xs text-gray-400">
            정산액 = (해당 월 기여 확정 결제자 − 환불자) × 단가. 이전 달 결제
            건의 환불은 해당 월에 음수(차감)로 잡혀요.
          </p>
        </>
      )}
    </section>
  );
}
